import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {FieldDefinitionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {fieldDefinitionTypeToFieldType} from 'schemas/fields/FieldDefinitionCompatibilityUtils';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

import {
  AntibodyEnforcedSchemaFieldSpec,
  AntibodyEnforcedSchemaSpec,
} from './AntibodyEnforcedDataModelConstants';
import {
  ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME,
  AntibodyComponentSchemaInterfaceApiId,
  isAntibodyComponentSchemaInterfaceId,
} from './AntibodySchemaInterfaceConstants';

/** Returns true if a field was applied by a different antibody component interface and should be removed. */
function shouldRemoveField(
  field: SchemaField | ClientSchemaField,
  appliedInterfaceId: AntibodyComponentSchemaInterfaceApiId | null
): boolean {
  return (
    field.appliedFieldset?.id != null &&
    field.appliedFieldset.id !== appliedInterfaceId &&
    isAntibodyComponentSchemaInterfaceId(field.appliedFieldset.id)
  );
}

/** Strips fields that were applied by any antibody component interface, returning only user-defined fields. */
export function removeAntibodyEnforcedFields<T extends SchemaField | ClientSchemaField>(
  existingFields: Array<T>
): Array<T> {
  return existingFields.filter((field) => !shouldRemoveField(field, null));
}

/**
 * Returns the names of user-defined fields that conflict with an enforced schema spec.
 * Display-name conflicts and system-name-only conflicts are returned separately so callers
 * can surface distinct error messages for each.
 */
export function getConflictingFieldNames(
  schemaSpec: AntibodyEnforcedSchemaSpec,
  existingFields: Array<SchemaField | ClientSchemaField>
): {displayNameConflicts: Array<string>; systemNameConflicts: Array<string>} {
  const enforcedFieldNameSet = new Set(schemaSpec.enforcedFieldSpecs.map((spec) => spec.fieldName));
  const enforcedSystemNameSet = new Set(schemaSpec.enforcedFieldSpecs.map((spec) => spec.systemName));
  const nonEnforcedFields = removeAntibodyEnforcedFields(existingFields);

  const displayNameConflicts: Array<string> = [];
  const systemNameConflicts: Array<string> = [];
  for (const field of nonEnforcedFields) {
    if (enforcedFieldNameSet.has(field.name)) {
      displayNameConflicts.push(field.name);
    }
    if (enforcedSystemNameSet.has(field.systemName)) {
      systemNameConflicts.push(field.systemName);
    }
  }
  return {displayNameConflicts, systemNameConflicts};
}

/** Converts an enforced field spec into a `ClientSchemaField` ready for use in the schema editor. */
export function enforcedFieldSpecToField(
  interfaceId: AntibodyComponentSchemaInterfaceApiId,
  enforcedFieldSpec: AntibodyEnforcedSchemaFieldSpec,
  registryId: string
): ClientSchemaField {
  const compatibleDropdowns = enforcedFieldSpec.dropdown?.compatibleDropdowns;
  const validDropdown =
    compatibleDropdowns != null
      ? compatibleDropdowns.find((dropdown) => dropdown.registry.id === registryId)
      : null;
  assert(
    !(enforcedFieldSpec.fieldType === FieldDefinitionType.DROPDOWN && validDropdown == null),
    'No compatible dropdown definition found'
  );
  const dropdown = validDropdown ?? null;
  return {
    cid: uuidv4(),
    name: enforcedFieldSpec.fieldName,
    systemName: enforcedFieldSpec.systemName,
    displayName: enforcedFieldSpec.fieldName,
    fieldType: fieldDefinitionTypeToFieldType(enforcedFieldSpec.fieldType),
    isRequired: enforcedFieldSpec.isRequired,
    isMulti: enforcedFieldSpec.isMulti,
    isComputed: false,
    isConvertedFromLink: false,
    isSnapshot: false,
    isIntegration: false,
    isParentLink: false,
    requiredLink:
      enforcedFieldSpec.linkedInterface == null
        ? null
        : {
            tagSchema: null,
            schemaInterface: {
              id: enforcedFieldSpec.linkedInterface.id,
              name: enforcedFieldSpec.linkedInterface.name,
            },
            folderItemType: null,
            entitySchemaFieldPath: null,
          },
    schemaFieldSelectorId: dropdown?.id ?? null,
    archiveRecord: null,
    unitApiIdentifier: enforcedFieldSpec.unit?.id ?? null,
    appliedFieldset: {
      id: interfaceId,
      name: ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[interfaceId],
    },
  };
}
